import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import { ImageResize } from 'quill-image-resize-module-ts';
import { FormError } from '../types/ErrorResponse.type';
import { FieldErrors } from './FieldErrors';
import 'react-quill/dist/quill.snow.css';
import { Blog } from '../../types/Blog.type';

const SANITY_PROJECT_ID = 'urerbr0z';
const SANITY_DATASET = 'production';
const SANITY_API_VERSION = '2024-01-01';

Quill.register('modules/imageResize', ImageResize);

type Props = {
	name: string;
	label: string;
	value?: any;
	onChange: (val: string) => void;
	errors?: FormError;
	className?: string;
	disabled?: boolean;
	props?: any;
	onImageUpload?: () => void;
	patchValue: (val: Partial<Blog>) => void;
};

type FloatingToolbarState = {
	top: number;
	left: number;
	formats: Record<string, any>;
	range: { index: number; length: number };
} | null;

const TOOLBAR_HEIGHT = 40; // approximate px height of floating toolbar

const WYSIWYG = ({
	name,
	label,
	value,
	onChange,
	errors,
	className,
	disabled,
	props = {},
	patchValue,
}: Props) => {
	const quillRef = useRef<any>(null);
	const editorWrapperRef = useRef<HTMLDivElement>(null);
	const floatingRef = useRef<HTMLDivElement>(null);
	const linkInputRef = useRef<HTMLInputElement>(null);
	// Ref so the selection-change handler can synchronously check link mode
	// without depending on stale closure state
	const linkModeActiveRef = useRef(false);

	const [floatingToolbar, setFloatingToolbar] = useState<FloatingToolbarState>(null);
	const [linkMode, setLinkMode] = useState(false);
	const [linkUrl, setLinkUrl] = useState('');

	// Focus the link input whenever link mode opens (without using autoFocus,
	// which would trigger a Quill selection-change and hide the toolbar)
	useEffect(() => {
		if (linkMode) {
			linkInputRef.current?.focus();
		}
	}, [linkMode]);

	// Dismiss the floating toolbar when the user clicks outside it while in link mode
	useEffect(() => {
		if (!linkMode) return;
		const handleOutside = (e: MouseEvent) => {
			if (floatingRef.current && !floatingRef.current.contains(e.target as Node)) {
				linkModeActiveRef.current = false;
				setLinkMode(false);
				setFloatingToolbar(null);
			}
		};
		document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, [linkMode]);

	// Listen for selection changes to show/hide the floating toolbar
	useEffect(() => {
		const quill = quillRef.current?.getEditor();
		if (!quill || disabled) return;

		const onSelectionChange = (range: any) => {
			if (!range || range.length === 0) {
				// Don't dismiss when focus moves to the link URL input
				if (linkModeActiveRef.current) return;
				setFloatingToolbar(null);
				setLinkMode(false);
				return;
			}

			// getBounds returns coords relative to the quill scroll container
			const bounds = quill.getBounds(range.index, range.length);
			const wrapper = editorWrapperRef.current;
			if (!bounds || !wrapper) return;

			// Offset by the wrapper's scroll + the ql-container's position inside the wrapper
			const container = wrapper.querySelector('.ql-container') as HTMLElement;
			const containerTop = container ? container.offsetTop : 0;

			const top = containerTop + bounds.top - TOOLBAR_HEIGHT - 6;
			const left = bounds.left + bounds.width / 2;

			setFloatingToolbar({
				top,
				left,
				formats: quill.getFormat(range.index, range.length),
				range: { index: range.index, length: range.length },
			});
			setLinkMode(false);
			setLinkUrl('');
		};

		quill.on('selection-change', onSelectionChange);
		return () => quill.off('selection-change', onSelectionChange);
	}, [disabled]);

	// Prevent mousedown on the floating toolbar from stealing focus/selection
	const stopBlur = (e: React.MouseEvent) => e.preventDefault();

	const applyFormat = (format: string, value: any) => {
		const quill = quillRef.current?.getEditor();
		if (!quill || !floatingToolbar) return;
		// formatText applies to a range without moving the selection,
		// avoiding the browser scroll-to-cursor that setSelection triggers
		quill.formatText(floatingToolbar.range.index, floatingToolbar.range.length, format, value, 'user');
		const updatedFormats = quill.getFormat(floatingToolbar.range.index, floatingToolbar.range.length);
		setFloatingToolbar((prev) => prev ? { ...prev, formats: updatedFormats } : null);
	};

	const handleLinkApply = () => {
		const quill = quillRef.current?.getEditor();
		if (!quill || !floatingToolbar) return;
		const url = linkUrl.trim();
		quill.formatText(floatingToolbar.range.index, floatingToolbar.range.length, 'link', url || false, 'user');
		linkModeActiveRef.current = false;
		setLinkMode(false);
		setLinkUrl('');
		setFloatingToolbar(null);
	};

	const handleLinkRemove = () => {
		const quill = quillRef.current?.getEditor();
		if (!quill || !floatingToolbar) return;
		quill.formatText(floatingToolbar.range.index, floatingToolbar.range.length, 'link', false, 'user');
		linkModeActiveRef.current = false;
		setLinkMode(false);
		setLinkUrl('');
		setFloatingToolbar(null);
	};

	const openLinkMode = () => {
		const existing = floatingToolbar?.formats?.link || '';
		setLinkUrl(existing);
		linkModeActiveRef.current = true;
		setLinkMode(true);
	};

	const modules = useMemo(() => {
		const handleImages = async () => {
			const quill = quillRef.current?.getEditor();
			if (!quill) return;

			// Save cursor position before file dialog opens (focus is lost when dialog opens)
			const range = quill.getSelection() || { index: quill.getLength() - 1 };

			const input = document.createElement('input');
			input.setAttribute('type', 'file');
			input.setAttribute('accept', 'image/*');
			input.setAttribute('multiple', 'true');
			input.click();

			input.onchange = async () => {
				const files = Array.from(input.files || []);
				if (!files.length) return;

				const token = process.env.REACT_APP_SANITY_TOKEN;
				if (!token) {
					console.error('REACT_APP_SANITY_TOKEN is not set.');
					return;
				}

				const uploadFile = async (file: File): Promise<string> => {
					const response = await fetch(
						`https://${SANITY_PROJECT_ID}.api.sanity.io/v${SANITY_API_VERSION}/assets/images/${SANITY_DATASET}`,
						{
							method: 'POST',
							headers: {
								Authorization: `Bearer ${token}`,
								'Content-Type': file.type || 'application/octet-stream',
							},
							body: file,
						}
					);
					if (!response.ok) {
						const text = await response.text();
						throw new Error(`Upload failed (${response.status}): ${text}`);
					}
					const data = await response.json();
					const url = data.document?.url ?? data.url;
					if (!url) throw new Error('No URL returned from Sanity.');
					return url;
				};

				try {
					// Upload all files in parallel
					const urls = await Promise.all(files.map(uploadFile));

					// Build a single Delta that inserts each image followed by an explicit \n.
					// This ensures every image lands in its own <p> block, all truly adjacent
					// (no empty paragraphs between them), so the customer site's consecutive-
					// image detection correctly groups them into a carousel.
					const Delta = Quill.import('delta');
					const delta = new Delta().retain(range.index);
					for (const url of urls) {
						delta.insert({ image: url });
						delta.insert('\n');
					}
					quill.updateContents(delta, 'user');
					quill.setSelection(range.index + urls.length * 2, 0, 'user');
				} catch (err: any) {
					console.error('Image upload failed:', err);
					alert(`Image upload failed: ${err?.message || 'Unknown error'}`);
				}
			};
		};
		return {
			toolbar: {
				container: [
					[{ header: [1, 2, 3, 4, 5, 6, false] }],
					['bold', 'italic', 'underline'],
					[{ list: 'ordered' }, { list: 'bullet' }],
					[{ align: [] }],
					['link', 'image'],
					['clean'],
					[{ color: [] }],
				],
				handlers: {
					image: handleImages,
				},
			},
			imageResize: {
				modules: ['Resize', 'DisplaySize'],
			},
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fmt = floatingToolbar?.formats ?? {};

	return (
		<div className={className}>
			{label && (
				<div>
					<label
						htmlFor={name}
						className="block text-sm font-medium leading-5 text-gray-900  dark:text-darkGray-200 sm:mt-px sm:pt-2"
					>
						{label}
					</label>
				</div>
			)}
			<div
				ref={editorWrapperRef}
				className={`relative ${disabled ? 'cursor-not-allowed' : ''}`}
				data-text-editor="name"
			>
				{/* Floating inline toolbar — appears above selected text */}
				{floatingToolbar && !disabled && (
					<div
						ref={floatingRef}
						onMouseDown={stopBlur}
						style={{
							position: 'absolute',
							top: floatingToolbar.top,
							left: floatingToolbar.left,
							transform: 'translateX(-50%)',
							zIndex: 9999,
						}}
						className="flex items-center bg-gray-900 text-white rounded-lg shadow-xl px-1 py-1 gap-0.5 text-sm"
					>
						{linkMode ? (
							/* Link URL input mode */
							<>
								<input
									ref={linkInputRef}
									type="url"
									placeholder="https://..."
									value={linkUrl}
									onChange={(e) => setLinkUrl(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') handleLinkApply();
										if (e.key === 'Escape') {
											linkModeActiveRef.current = false;
											setLinkMode(false);
											setLinkUrl('');
											setFloatingToolbar(null);
										}
									}}
									className="bg-gray-800 text-white text-xs rounded px-2 py-1 w-52 outline-none border border-gray-600 focus:border-blue-400"
								/>
								<button
									onMouseDown={stopBlur}
									onClick={handleLinkApply}
									title="Apply link"
									className="px-2 py-1 rounded hover:bg-gray-700 text-green-400 font-bold"
								>
									✓
								</button>
								{fmt.link && (
									<button
										onMouseDown={stopBlur}
										onClick={handleLinkRemove}
										title="Remove link"
										className="px-2 py-1 rounded hover:bg-gray-700 text-red-400"
									>
										✕
									</button>
								)}
								<button
									onMouseDown={stopBlur}
									onClick={() => {
										linkModeActiveRef.current = false;
										setLinkMode(false);
										setLinkUrl('');
										setFloatingToolbar(null);
									}}
									title="Cancel"
									className="px-2 py-1 rounded hover:bg-gray-700 text-gray-400"
								>
									↩
								</button>
							</>
						) : (
							/* Default format buttons */
							<>
								<button
									onMouseDown={stopBlur}
									onClick={() => applyFormat('bold', !fmt.bold)}
									title="Bold"
									className={`px-2 py-1 rounded font-bold hover:bg-gray-700 ${fmt.bold ? 'bg-gray-600' : ''}`}
								>
									B
								</button>
								<button
									onMouseDown={stopBlur}
									onClick={() => applyFormat('italic', !fmt.italic)}
									title="Italic"
									className={`px-2 py-1 rounded italic hover:bg-gray-700 ${fmt.italic ? 'bg-gray-600' : ''}`}
								>
									I
								</button>
								<button
									onMouseDown={stopBlur}
									onClick={() => applyFormat('underline', !fmt.underline)}
									title="Underline"
									className={`px-2 py-1 rounded underline hover:bg-gray-700 ${fmt.underline ? 'bg-gray-600' : ''}`}
								>
									U
								</button>
								<div className="w-px h-5 bg-gray-600 mx-0.5" />
								<button
									onMouseDown={stopBlur}
									onClick={openLinkMode}
									title={fmt.link ? 'Edit link' : 'Add link'}
									className={`px-2 py-1 rounded hover:bg-gray-700 ${fmt.link ? 'bg-gray-600 text-blue-400' : ''}`}
								>
									🔗
								</button>
								{fmt.link && (
									<button
										onMouseDown={stopBlur}
										onClick={handleLinkRemove}
										title="Remove link"
										className="px-2 py-1 rounded hover:bg-gray-700 text-red-400 text-xs"
									>
										✕
									</button>
								)}
							</>
						)}
					</div>
				)}

				<ReactQuill
					id={name}
					theme="snow"
					value={value}
					onChange={onChange}
					className="mt-1 rounded-md shadow-sm"
					{...props}
					readOnly={disabled}
					bounds={`[data-text-editor="name"]`}
					modules={modules}
					ref={quillRef}
				/>
				<FieldErrors errors={errors} />
			</div>
		</div>
	);
};

export default WYSIWYG;
